import React, { memo, useCallback } from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { RectButton } from 'react-native-gesture-handler';

import { BackspaceIcon } from '@/assets/icons/BackspaceIcon';
import { ds } from '@/components/lib/design.system';

import { Text } from './TextComponent';
import { Box, Row } from './lib/components';

const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['.', '0', 'back'],
] as const;

type NumberPadProps = {
    onNumberPress: (number: number) => void;
    onDecimalPress: () => void;
    onBackspacePress: () => void;
    keyboardStyle?: StyleProp<ViewStyle>;
    keyStyle?: StyleProp<ViewStyle>;
    keyLabelStyle?: StyleProp<ViewStyle>;
    hideBackSpace?: boolean;
    decimalsEnabled?: boolean;
};

export const NumberPad = memo(
    ({
        onNumberPress,
        onDecimalPress,
        onBackspacePress,
        keyboardStyle,
        keyStyle,
        keyLabelStyle,
        hideBackSpace = false,
        decimalsEnabled,
    }: NumberPadProps) => {
        const handleKeyPress = useCallback(
            (key: string) => {
                if (key === 'back') {
                    onBackspacePress();
                } else if (key === '.') {
                    onDecimalPress();
                } else {
                    onNumberPress(Number(key));
                }
            },
            [onBackspacePress, onDecimalPress, onNumberPress]
        );

        return (
            <Box gap={'s8'} style={keyboardStyle} backgroundColor={ds.colorToken.background.neutral.subtler}>
                {keys.map((row, rowIndex) => (
                    <Row key={rowIndex} gap={'s8'}>
                        {row.map(key => {
                            if (key === '.' && !decimalsEnabled) {
                                return <Box key={'decimal-placeholder'} flex={1} />;
                            }
                            const bgColor =
                                key === 'back' || key === '.'
                                    ? 'transparent'
                                    : ds.colorValue.background.neutral.subtler;
                            return (
                                <RectButton
                                    key={key}
                                    style={[styles.button, { backgroundColor: bgColor }, keyStyle]}
                                    onPress={() => {
                                        handleKeyPress(key);
                                    }}
                                >
                                    {key === 'back' ? (
                                        hideBackSpace ? null : (
                                            <BackspaceIcon />
                                        )
                                    ) : (
                                        <Text
                                            style={keyLabelStyle}
                                            fontSize={24}
                                            color={ds.colorToken.text.primary}
                                            fontWeight={'600'}
                                        >
                                            {key}
                                        </Text>
                                    )}
                                </RectButton>
                            );
                        })}
                    </Row>
                ))}
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    button: {
        flex: 1,
        paddingVertical: 8,
        alignItems: 'center',
        borderRadius: 12,
    },
});
